
import { defineComponent, h, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import HoundSelectModal from './HoundSelectModal.vue';
import { formatNumberDecimals, HoundModel, QueueModel, RaceModel } from '../../../common';
import { config } from '@/config';
import { default as dayjs } from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { default as duration } from 'dayjs/plugin/duration';
import Timer from './Timer.vue';
import { EthersService as eth, http, convertToUSD } from '../services';
import { notification, toast } from '@/utils/notification.util';
import BaseNotificationContent from './shared/BaseNotificationContent.vue';
import { Redo as RefreshIcon } from '@vicons/fa';
import { exchangeRatesAvailable, exchangeRates } from '../data/global';
import breakpoints from '../utils/breakpoints';

dayjs.extend(utc);
dayjs.extend(duration);

enum RaceState {
  UPCOMING='upcoming',
  LIVE='live',
  ENDED='ended'
};

export default defineComponent({
  name: 'RaceInfo',
  emits: ['join', 'unenqueue'],
  props: {
    race: {
      type: Object as PropType<QueueModel|RaceModel>,
      required: true
    },
    raceType: {
      type: String
    },
    pauseAnimations: {
      type: Boolean,
      default: false
    }
  },
  components: { HoundSelectModal, Timer, BaseNotificationContent, RefreshIcon },
  setup(props, { emit }) {
    const show = ref(props.pauseAnimations);
    const info = ref<QueueModel>();
    const participants = ref<HoundModel[]>([]);
    const showHoundSelectModal = ref(false);
    const raceStartDate = ref('');
    const minutesRemaining = ref(0);
    const winnings = ref<number[]>([]);
    const currencyName = ref<string>(null);
    let refreshTimeout = null;

    watch(() => props.race, (_) => {
      resetInfo(props.race as QueueModel|RaceModel);
    });

    onMounted(() => {
      resetInfo(props.race as QueueModel|RaceModel);
      if (props.raceType === 'join') {
        refreshTimeout = setTimeout(() => refresh(), 5000);
      }
    });

    const raceState = ref<RaceState>(RaceState.UPCOMING);
    const participantPlacement = ref([]);

    const resetInfo = (raceModel: QueueModel|RaceModel) => {
      info.value = null;
      participants.value = [];

      (raceModel as QueueModel).participants.forEach(e => {
        participants.value.push(new HoundModel(e));
      });
      info.value = raceModel as QueueModel;
      currencyName.value = eth.currenciesInfo.find(c => c.value === info.value.entryFeeCurrency)?.label;
      show.value = true;

      if (props.raceType === 'live') {
        const currentDate = dayjs().utc();
        const race = raceModel as RaceModel;
        const actualStartDate = dayjs(race.createdAt).utc().add(2, 'minute');
        const raceTime = parseInt(race.arena.distanceType().split('m')[0]) / 100 * 3;
        const actualRaceEndDate = actualStartDate.add(raceTime, 'second');
        raceStartDate.value = dayjs(race.createdAt).format('h:mm A (MMMM D, YYYY)');
        minutesRemaining.value = actualStartDate.diff(currentDate)/(1000 * 60);

        if (currentDate.isBefore(actualStartDate)) {
          raceState.value = RaceState.UPCOMING;
        } else if (currentDate.isBefore(actualRaceEndDate)) {
          raceState.value = RaceState.LIVE;
        } else {
          raceState.value = RaceState.ENDED;
        }

        participantPlacement.value = eth.utils.defaultAbiCoder.decode(['uint256[]'], race.ordered_participants)[0].map(p => Number(eth.utils.hexValue(p)).toString());

        if (raceState.value === RaceState.ENDED) {
          const auxParticipants = [];
          const pool = race.participants.length * Number(eth.utils.formatEther(race.entryFee));
          winnings.value = [
            pool * .5,
            pool * .3,
            pool * .2
          ];
          participants.value.forEach(p => {
            auxParticipants[participantPlacement.value.indexOf(p.token_id)] = p;
          });
          participants.value = auxParticipants;
        }
      }
    };

    const join = () => {
      showHoundSelectModal.value = true;
    }

    const refresh = async (hideLoading = true) => {
      clearTimeout(refreshTimeout);
      const updatedQueue = await http.getQueue((props.race as QueueModel).id.toString(), hideLoading);
      (updatedQueue !== undefined) && resetInfo(updatedQueue);
      refreshTimeout = setTimeout(() => refresh(), 5000);
    };

    onBeforeUnmount(() => {
      clearTimeout(refreshTimeout);
    });

    const unenqueue = async (hound: HoundModel) => {
      try {
        const tx = await eth.queues.unenqueue(
          (props.race as QueueModel).queue_id,
          hound.token_id,{
            gasLimit: 500000
          }
        );
        await tx.wait();
        notification.create({
            title: 'Left Queue',
            description: `You've successfully left ${(props.race as QueueModel).name}.`,
            content: () => h(
              BaseNotificationContent,
              {
                infoElements: [
                  { label: 'Queue', content: (props.race as QueueModel).name },
                  { label: 'Hound', content: hound.name }
                ]
              }
            ),
            meta: new Date().toUTCString()
          });
        toast.success('Successfully left the queue');
        emit('unenqueue');
      } catch (err) {
        console.log(err);
        toast.error('Transaction failed');
      }
    };

    const watchRace2D = () => {
      window.open(`${config.sim2dUrl}?raceId=${info.value.id}`);
    }

    const watchRace3D = () => {
      window.open(`${config.sim3dUrl}?raceId=${info.value.id}&noGui=true&antialias=true&lights=true`);
    }

    const onJoined = () => {
      emit('join');
    }

    const router = useRouter();
    const openHound = (id: string) => {
      const targetRoute = router.resolve({
        name: 'Hound',
        params: {
          id: id
        }
      }).fullPath;
      const basePath = window.location.origin;
      window.open(basePath + targetRoute);
    };

    const canLeave = (hound: HoundModel) => {
      return dayjs().isAfter(dayjs(hound.lastEnqueue).add(info.value.cooldown, 'seconds'));
    };
    
    return {
      info,
      participants,
      show,
      showHoundSelectModal,
      join,
      onJoined,
      formatNumberDecimals,
      raceType: props.raceType,
      watchRace2D,
      watchRace3D,
      raceState,
      raceStartDate,
      minutesRemaining,
      winnings,
      eth,
      ethReactive: eth.reactive,
      participantPlacement,
      unenqueue,
      currencyName,
      refresh,
      openHound,
      canLeave,
      convertToUSD,
      exchangeRatesAvailable,
      exchangeRates,
      breakpoints
    }
  }
});
